/**
 * Union Hub activity_runs into the Console Staging/Prod Agents list.
 *
 * Console Agents is V1-scan-based (`/api/v1/scans/website/...`). Hub Activity
 * lists V2 `activity_runs`, including agentic runs that never linked a scan
 * (`scan_ids` empty/null). Those show on Hub with no pass/findings chips but
 * disappear from Console Agents — operators see "missing agents".
 *
 * This helper adds synthetic list rows for activity runs that are not already
 * represented by a V1 scan in `scans`. Runs whose `scan_ids` overlap the scan
 * list stamp `activity_run_id` / `activity_run_status` onto the matching scan
 * row (so Hub "canceled"/Stopped is visible to superusers on Console Agents)
 * and are otherwise skipped (the scan row stays the canonical Console entry).
 * Rows also carry `activity_run_agent_slug` and `activity_run_launcher_user_id`,
 * which the notification picker uses to preselect the agent's recipients plus
 * the launcher (CUS-1353).
 */

const getPrimaryScanId = (scan) =>
  scan?.id || scan?.scan_id || scan?.console_scan_id || null;

const runScanIds = (run) => {
  if (!Array.isArray(run?.scan_ids)) return [];
  return run.scan_ids.filter((id) => typeof id === "string" && id.trim());
};

const findingsCount = (run) => {
  if (typeof run?.fail_count === "number") return run.fail_count;
  if (typeof run?.findings === "number") return run.findings;
  if (Array.isArray(run?.findings)) return run.findings.length;
  return 0;
};

/**
 * @param {Array<object>} scans - V1 scan rows already shown in Agents
 * @param {Array<object>} activityRuns - Hub activity_runs cards for the same website/env
 * @returns {Array<object>} scans plus synthetic activity-run-only rows, newest first
 */
export function mergeActivityRunsIntoScans(scans = [], activityRuns = []) {
  const base = Array.isArray(scans) ? scans.filter(Boolean).map((s) => ({ ...s })) : [];
  const runs = Array.isArray(activityRuns) ? activityRuns.filter(Boolean) : [];

  const knownScanIds = new Set();
  const scanById = new Map();
  for (const scan of base) {
    const id = getPrimaryScanId(scan);
    if (id) {
      knownScanIds.add(id);
      scanById.set(id, scan);
    }
  }

  const extras = [];
  for (const run of runs) {
    const runId = run.id || run.run_id;
    if (!runId) continue;

    const linked = runScanIds(run);
    const matchedScan = linked.map((sid) => scanById.get(sid)).find(Boolean);
    if (matchedScan) {
      // Keep the V1 scan as the Console row, but stamp Hub lifecycle so
      // canceled/Stopped is visible to superusers (previously dropped here).
      matchedScan.activity_run_id = matchedScan.activity_run_id || runId;
      matchedScan.activity_run_status =
        run.status || matchedScan.activity_run_status || null;
      if (matchedScan.activity_run_id === runId) {
        matchedScan.activity_run_agent_slug = run.agent_slug || null;
        matchedScan.activity_run_launcher_user_id = run.triggered_by_user_id || null;
      }
      continue;
    }

    // Prefer a linked-but-missing scan_id as the row key when present so a
    // later scan materialization can still line up; otherwise key by run id.
    // Always mark extras as activity_run_only: by construction no V1 scan in
    // `scans` represents this run (linked ids were missing). Treating a
    // hollow scan_ids ref as a real V1 scan makes delete/select call
    // DELETE/GET /scans/{id} → "Scan {id} not found".
    const primaryId = linked[0] || runId;
    if (knownScanIds.has(primaryId)) continue;
    knownScanIds.add(primaryId);

    const count = findingsCount(run);
    extras.push({
      id: primaryId,
      scan_id: linked[0] || null,
      title: run.title || "Agent Run",
      start_time: run.ran_at || run.started_at || null,
      updated_at: run.ran_at || run.updated_at || null,
      issues_count: count,
      issues_ids: [],
      activity_run_id: runId,
      activity_run_only: true,
      activity_run_status: run.status || null,
      activity_run_agent_slug: run.agent_slug || null,
      activity_run_launcher_user_id: run.triggered_by_user_id || null,
      pass_count: typeof run.pass_count === "number" ? run.pass_count : 0,
      website_id: run.website_id || null,
    });
  }

  if (extras.length === 0) return base;

  const merged = [...base, ...extras];
  merged.sort((a, b) => {
    const dateA = new Date(a.start_time || a.updated_at || 0).getTime();
    const dateB = new Date(b.start_time || b.updated_at || 0).getTime();
    return dateB - dateA;
  });
  return merged;
}

/**
 * The newest run in `activityRuns` that lists `scanId` in its `scan_ids`.
 * A scan pushed to production keeps its id, so the staging run behind a prod
 * `legacy-run-*` (which has no agent or launcher) is found this way.
 */
export function findRunForScan(activityRuns = [], scanId) {
  if (!scanId || !Array.isArray(activityRuns)) return null;
  return (
    activityRuns.find((run) => run && runScanIds(run).includes(scanId)) || null
  );
}

export default mergeActivityRunsIntoScans;
